import React, { useState, useEffect } from 'react';
import { Shield, Award, Clock, Users, CheckCircle, TrendingUp, Mail, User, Phone, MessageSquare, FileCheck, Scale, Globe, Zap, Target, Lock, ArrowRight, Star } from 'lucide-react';
import PricingSection from '../components/PricingSection';
import TestimonialsSlider from '../components/TestimonialsSlider';
import ProcessSteps from '../components/ProcessSteps';
import UniversalPopupForm from '../components/UniversalPopupForm';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import { submitFormWithGeolocation, getGeoLocation } from '../services/apiService';

const Home = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeo = async () => {
      const data = await getGeoLocation();
      if (data?.isSuccess) {
        console.log('User location:', data.data.countryName);
      }
    };
    fetchGeo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await submitFormWithGeolocation(formData);
    
    if (!result.success) {
      toast.error(result.error);
      setLoading(false);
    }
    // If success, user will be redirected automatically
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Fileincanada | Canada Trademark Search and Filing"
        description="Fileincanada offers simple Canada filing and tracking with simple guidance. Move from idea to registered mark."
        keywords="Canadian trademark registration, CIPO trademark, register trademark Canada, brand protection, trademark filing, intellectual property Canada"
        appendSiteName={false}
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden bg-gradient-to-br from-slate-900 via-teal-900 to-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/5669619/pexels-photo-5669619.jpeg"
            alt="Legal scales of justice"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-teal-900/90 to-slate-900/95"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="text-white">
              <div className="inline-flex items-center bg-teal-600/30 backdrop-blur-sm rounded-full px-5 py-2.5 mb-8 border border-teal-400/30">
                <Star className="w-4 h-4 text-yellow-400 mr-2" />
                <span className="text-white font-bold text-sm">Trusted CIPO Trademark Specialists</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Protect Your Brand.
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                  Dominate Your Market.
                </span>
              </h1>
              <p className="text-xl text-slate-200 leading-relaxed mb-8 leading-relaxed font-light">
                Register your trademark with Canada's CIPO and secure exclusive rights to your brand nationwide. Expert guidance. Fast filing. Maximum protection.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-2xl opacity-75 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative bg-slate-900 rounded-2xl p-5 border-2 border-yellow-400/50 transform group-hover:scale-105 transition-all duration-300">
                    <div className="flex items-center justify-between mb-2">
                      <Award className="w-7 h-7 text-yellow-400" />
                      <div className="text-4xl font-bold text-white" style={{ color: '#facc15' }}>98<span className="text-yellow-400">%</span></div>
                    </div>
                    <div className="text-sm text-slate-300 font-semibold">Success Rate</div>
                  </div>
                </div>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-400 to-emerald-400 rounded-2xl opacity-75 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative bg-slate-900 rounded-2xl p-5 border-2 border-teal-400/50 transform group-hover:scale-105 transition-all duration-300">
                    <div className="flex items-center justify-between mb-2">
                      <Clock className="w-7 h-7 text-teal-400" />
                      <div className="text-4xl font-bold" style={{ color: '#2dd4bf' }}>24<span className="text-teal-400">h</span></div>
                    </div>
                    <div className="text-sm text-slate-300 font-semibold">Response Time</div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setIsPopupOpen(true)}
                  className="group bg-teal-600 text-white font-bold px-8 py-4 rounded-xl hover:bg-yellow-400 hover:text-slate-900 transform hover:scale-105 transition-all duration-300 shadow-2xl flex items-center justify-center"
                >
                  <span>Start Registration</span>
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  className="js-chat border-2 border-white text-white font-bold px-8 py-4 rounded-xl hover:bg-yellow-400 hover:text-slate-900 transition-all duration-300 flex items-center justify-center"
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  <span>Chat with Expert</span>
                </button>
              </div>
            </div>

            {/* Right Side - Redesigned Compact Form */}
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-teal-400 to-yellow-400 rounded-3xl opacity-30 blur-3xl animate-pulse"></div>
              
              <div className="relative bg-white rounded-2xl shadow-2xl p-8 border-l-4 border-yellow-400">
                {/* Header Badge */}
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-slate-900 px-5 py-1.5 rounded-full font-bold text-xs shadow-lg flex items-center space-x-1.5">
                    <Zap className="w-3.5 h-3.5" />
                    <span>Quick Start</span>
                  </div>
                </div>

                <div className="text-center mt-3 mb-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    Start Your <span className="text-teal-600">Protection</span>
                  </h2>
                  <p className="text-slate-600 text-sm">Join 650+ protected brands in Canada</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3.5">
                  {/* Name Field */}
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-teal-600">
                      <User className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white transition-all placeholder-slate-400"
                      placeholder="Your Full Name"
                    />
                  </div>

                  {/* Email Field */}
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-teal-600">
                      <Mail className="w-4 h-4" />
                    </div>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white transition-all placeholder-slate-400"
                      placeholder="your@email.com"
                    />
                  </div>

                  {/* Phone Field */}
                  <div className="flex gap-2">
                    <select
                      value={formData.countryCode}
                      onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                      className="w-auto px-2 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white transition-all text-sm"
                    >
                      <option value="+1">+1 US/Canada</option>
                      <option value="+44">+44 UK</option>
                      <option value="+91">+91 India</option>
                      <option value="+86">+86 China</option>
                      <option value="+81">+81 Japan</option>
                      <option value="+82">+82 South Korea</option>
                      <option value="+61">+61 Australia</option>
                      <option value="+49">+49 Germany</option>
                      <option value="+33">+33 France</option>
                      <option value="+39">+39 Italy</option>
                      <option value="+34">+34 Spain</option>
                      <option value="+7">+7 Russia</option>
                      <option value="+55">+55 Brazil</option>
                      <option value="+52">+52 Mexico</option>
                      <option value="+27">+27 South Africa</option>
                      <option value="+971">+971 UAE</option>
                      <option value="+65">+65 Singapore</option>
                      <option value="+31">+31 Netherlands</option>
                      <option value="+46">+46 Sweden</option>
                    </select>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 10) {
                          setFormData({ ...formData, phone: value });
                        }
                      }}
                      className="flex-1 px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white transition-all placeholder-slate-400"
                      placeholder="888 752 5038"
                      maxLength="10"
                    />
                  </div>

                  {/* Message Field - Smaller */}
                  <div className="relative">
                    <div className="absolute left-3.5 top-3.5 text-teal-600">
                      <MessageSquare className="w-4 h-4" />
                    </div>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows="2"
                      className="w-full pl-11 pr-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white transition-all resize-none placeholder-slate-400 text-sm"
                      placeholder="Tell us about your trademark (optional)..."
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white font-bold py-3.5 px-6 rounded-xl hover:bg-yellow-400 hover:text-slate-900 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 shadow-xl flex items-center justify-center space-x-2 group"
                  >
                    <Shield className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    <span className="text-base">{loading ? 'Processing...' : 'Get Protected Now'}</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>

                  {/* Compact Trust Indicators */}
                  <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-200">
                    <div className="text-center">
                      <div className="text-lg font-bold text-teal-600">98%</div>
                      <div className="text-xs text-slate-600">Success</div>
                    </div>
                    <div className="text-center border-l border-r border-slate-200">
                      <div className="text-lg font-bold text-teal-600">24h</div>
                      <div className="text-xs text-slate-600">Response</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-teal-600">650+</div>
                      <div className="text-xs text-slate-600">Filed</div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-16 bg-white border-y-4 border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-teal-700 mb-2">650+</div>
              <div className="text-slate-600 font-semibold">Trademarks Filed</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-teal-700 mb-2">98%</div>
              <div className="text-slate-600 font-semibold">Approval Rate</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-teal-700 mb-2">24h</div>
              <div className="text-slate-600 font-semibold">Fast Response</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-teal-700 mb-2">100%</div>
              <div className="text-slate-600 font-semibold">CIPO Focused</div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1 - Expertise */}
      <section className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -inset-8 bg-gradient-to-r from-blue-600 to-slate-700 rounded-3xl opacity-20 blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1479142506502-19b3a3b7ff33"
                alt="Legal authority and expertise"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover border-4 border-white"
              />
            </div>

            <div>
              <div className="inline-block bg-teal-600 text-white rounded-full px-6 py-2 mb-6 text-sm font-bold uppercase tracking-wider">
                CIPO Specialists
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">
                Master the CIPO<br />Trademark Process
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                The Canadian Intellectual Property Office (CIPO) sets strict standards. Our experts have successfully filed over 650 trademarks and understand every requirement to ensure your application succeeds.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-600 rounded-xl p-3 flex-shrink-0">
                    <FileCheck className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2 text-lg">Precise Documentation</h3>
                    <p className="text-slate-600">Every CIPO requirement met with absolute precision. Zero rejection risk.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-600 rounded-xl p-3 flex-shrink-0">
                    <Zap className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2 text-lg">Lightning-Fast Filing</h3>
                    <p className="text-slate-600">Streamlined process gets your trademark application filed quickly and correctly.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-600 rounded-xl p-3 flex-shrink-0">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2 text-lg">Strategic Classification</h3>
                    <p className="text-slate-600">Expert class selection ensures complete protection for your brand.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 - Protection */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-white0 text-slate-900 rounded-full px-6 py-2 mb-6 text-sm font-bold uppercase tracking-wider">
                Maximum Protection
              </div>
              <h2 className="text-4xl font-bold mb-6 leading-tight">
                Own Your Brand.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                  Control Your Future.
                </span>
              </h2>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                A registered CIPO trademark gives you exclusive rights across every Canadian province and territory. Stop competitors from stealing your identity.
              </p>
              <div className="bg-gradient-to-br from-teal-600/30 to-slate-800/30 backdrop-blur-sm rounded-3xl p-8 mb-8 border-2 border-blue-500/30">
                <h3 className="font-bold text-white mb-6 text-xl">Your Protection Package:</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="bg-white0 rounded-full p-1">
                      <CheckCircle className="w-5 h-5 text-slate-900" />
                    </div>
                    <span className="text-slate-200 font-semibold">Exclusive rights coast-to-coast in Canada</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-white0 rounded-full p-1">
                      <CheckCircle className="w-5 h-5 text-slate-900" />
                    </div>
                    <span className="text-slate-200 font-semibold">Legal power to stop brand infringement</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-white0 rounded-full p-1">
                      <CheckCircle className="w-5 h-5 text-slate-900" />
                    </div>
                    <span className="text-slate-200 font-semibold">10-year protection (renewable indefinitely)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-white0 rounded-full p-1">
                      <CheckCircle className="w-5 h-5 text-slate-900" />
                    </div>
                    <span className="text-slate-200 font-semibold">Valuable asset for licensing and business growth</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsPopupOpen(true)}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-900 font-bold px-10 py-4 rounded-xl hover:from-yellow-400 hover:to-yellow-500 transform hover:scale-105 transition-all duration-300 shadow-2xl"
              >
                Secure My Brand Now
              </button>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-8 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-3xl opacity-30 blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1479142506502-19b3a3b7ff33"
                alt="Legal professional expert"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover border-4 border-slate-700"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 - Why Professional Help */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -inset-8 bg-gradient-to-r from-slate-700 to-blue-700 rounded-3xl opacity-20 blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1479142506502-19b3a3b7ff33"
                alt="Professional business consultation"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover border-4 border-white"
              />
            </div>

            <div>
              <div className="inline-block bg-red-600 text-white rounded-full px-6 py-2 mb-6 text-sm font-bold uppercase tracking-wider">
                Expert Advantage
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">
                DIY Trademarks<br />
                <span className="text-red-600">Cost More</span> Than You Think
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Filing a trademark yourself has a 40% rejection rate. Every rejection means lost time, lost fees, and lost protection. Our experts get it right the first time.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-10">
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-6 border-4 border-red-200">
                  <div className="text-5xl font-bold text-red-600 mb-2">40%</div>
                  <div className="text-sm text-slate-700 font-bold">DIY Failure Rate</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border-4 border-green-200">
                  <div className="text-5xl font-bold text-green-600 mb-2">98%</div>
                  <div className="text-sm text-slate-700 font-bold">Our Success Rate</div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700 font-semibold">Avoid expensive mistakes and wasted CIPO fees</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700 font-semibold">Save months of back-and-forth revisions</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700 font-semibold">Expert guidance from day one to approval</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-teal-600 text-white rounded-full px-6 py-2 mb-6 text-sm font-bold uppercase tracking-wider">
              The File In Canada Advantage
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Why Canadian Businesses Choose Us
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We specialize exclusively in Canadian trademark law and CIPO procedures
            </p>
          </div>

          <div className="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
            <div className="bg-white rounded-2xl p-8 border-4 border-teal-600/20 hover:border-teal-600 transition-all duration-300 min-w-[85%] md:min-w-0 snap-center">
              <div className="bg-teal-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">CIPO Masters</h3>
              <p className="text-slate-600 leading-relaxed font-medium">
                Deep expertise in every CIPO requirement and procedure ensures flawless registration.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border-4 border-teal-600/20 hover:border-teal-600 transition-all duration-300 min-w-[85%] md:min-w-0 snap-center">
              <div className="bg-teal-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Proven Results</h3>
              <p className="text-slate-600 leading-relaxed font-medium">
                98% approval rate through comprehensive searches and expert application prep.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border-4 border-teal-600/20 hover:border-teal-600 transition-all duration-300 min-w-[85%] md:min-w-0 snap-center">
              <div className="bg-teal-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Speed Matters</h3>
              <p className="text-slate-600 leading-relaxed font-medium">
                24-hour response time and efficient CIPO filing gets you protected fast.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border-4 border-teal-600/20 hover:border-teal-600 transition-all duration-300 min-w-[85%] md:min-w-0 snap-center">
              <div className="bg-teal-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Personal Touch</h3>
              <p className="text-slate-600 leading-relaxed font-medium">
                Dedicated case managers guide you through every step of the process.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border-4 border-teal-600/20 hover:border-teal-600 transition-all duration-300 min-w-[85%] md:min-w-0 snap-center">
              <div className="bg-teal-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Thorough Search</h3>
              <p className="text-slate-600 leading-relaxed font-medium">
                Complete database searches prevent conflicts and ensure trademark uniqueness.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border-4 border-teal-600/20 hover:border-teal-600 transition-all duration-300 min-w-[85%] md:min-w-0 snap-center">
              <div className="bg-teal-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">24/7 Monitoring</h3>
              <p className="text-slate-600 leading-relaxed font-medium">
                Continuous brand monitoring protects against potential infringements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <ProcessSteps />

      {/* Pricing Section */}
      <div id="pricing">
        <PricingSection />
      </div>

      {/* Testimonials */}
      <TestimonialsSlider />

      {/* Final CTA */}
      <section className="relative py-32 overflow-hidden bg-gradient-to-br from-slate-900 via-teal-900 to-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/2916828/pexels-photo-2916828.jpeg"
            alt="Canadian flag"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-teal-900/90 to-slate-900/95"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center bg-teal-600/30 backdrop-blur-sm rounded-full px-8 py-3 mb-10 border-2 border-teal-400/50">
            <Star className="w-5 h-5 text-yellow-400 mr-2" />
            <span className="text-white font-bold text-lg">Canada's Premier Trademark Service</span>
          </div>

          <h2 className="text-5xl md:text-6xl font-bold text-white mb-8 leading-tight">
            Your Brand.<br />
            Your Rights.
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
              Your Success.
            </span>
          </h2>
          
          <p className="text-2xl md:text-3xl text-slate-200 mb-14 max-w-4xl mx-auto leading-relaxed font-light">
            Join 650+ Canadian businesses protecting their most valuable asset with official CIPO registration.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            <button
              onClick={() => setIsPopupOpen(true)}
              className="group bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-900 font-bold px-12 py-6 rounded-2xl hover:from-yellow-400 hover:to-yellow-500 transform hover:scale-110 transition-all duration-300 shadow-2xl text-xl"
            >
              <span className="flex items-center space-x-3">
                <Shield className="w-7 h-7" />
                <span>Start Your Protection</span>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
              </span>
            </button>
            
            <button
              className="js-chat border-4 border-white text-white font-bold px-12 py-6 rounded-2xl hover:bg-yellow-400 hover:text-slate-900 transition-all duration-300 shadow-2xl text-xl backdrop-blur-sm"
            >
              <span className="flex items-center space-x-3">
                <MessageSquare className="w-6 h-6" />
                <span>Chat with Expert</span>
              </span>
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border-2 border-white/30">
              <div className="text-5xl font-bold text-white mb-3">650+</div>
              <div className="text-slate-200 font-semibold">Brands Protected</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border-2 border-white/30">
              <div className="text-5xl font-bold text-white mb-3">98%</div>
              <div className="text-slate-200 font-semibold">Success Rate</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border-2 border-white/30">
              <div className="text-5xl font-bold text-white mb-3">24hr</div>
              <div className="text-slate-200 font-semibold">Response Time</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border-2 border-white/30">
              <div className="text-5xl font-bold text-white mb-3">10+</div>
              <div className="text-slate-200 font-semibold">Provinces Served</div>
            </div>
          </div>

          <p className="text-slate-300 mt-16 text-lg font-semibold">
            Protecting Canadian businesses coast to coast • Official CIPO Registration Service
          </p>
        </div>
      </section>

      <UniversalPopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default Home;